import { api } from './api';

/** POS connections, team management, plan and usage. */

/**
 * One row per location, always — not one per connection. A location with no POS
 * still comes back, as provider NONE / status DISCONNECTED, so the page must
 * read `connectionId` rather than assume every row is a live integration.
 */
export interface PosConnectionRecord {
  locationId: string;
  locationName: string;
  provider: string;
  status: string;
  /** Present only when a connection exists; sync and disconnect address it. */
  connectionId?: string;
  merchantId?: string;
  connectedAt?: string;
  lastSyncAt?: string;
  lastSyncItemCount?: number;
  lastSyncError?: string;
  lastOrderSentAt?: string;
  lastOrderNumber?: string;
  lastOrderExternalId?: string;
  ordersNeedingAttention: number;
  capabilities: Record<string, boolean>;
}

export interface MembershipRecord {
  _id: string;
  role: string;
  status: string;
  locationIds: string[];
  userId: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    lastLoginAt?: string;
    emailVerifiedAt?: string;
  } | null;
}

export interface PlanRecord {
  _id: string;
  code: string;
  name: string;
  description?: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  includedMinutes: number;
  overageRatePerMinuteCents: number;
  maxLocations: number;
  features: string[];
  trialDays: number;
}

export interface SubscriptionResponse {
  subscription:
    | (Omit<PlanRecord, '_id'> & {
        _id: string;
        status: string;
        interval: string;
        currentPeriodEnd: string;
        cancelAtPeriodEnd: boolean;
        trialEndsAt?: string;
        planId: PlanRecord | null;
      })
    | null;
  usage: {
    period: string;
    includedMinutes: number;
    usedMinutes: number;
    remainingMinutes: number;
    usagePercent: number;
    overageMinutes: number;
    overageCostCents: number;
  };
}

export interface InvoiceRecord {
  _id: string;
  number?: string;
  status: string;
  totalCents: number;
  currency: string;
  createdAt: string;
  hostedInvoiceUrl?: string;
}

export const accountApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /* ----------------------------------------------------------------- POS */
    getPosConnections: builder.query<{ data: PosConnectionRecord[] }, void>({
      query: () => '/pos',
      providesTags: ['Pos']
    }),
    connectClover: builder.mutation<
      { connected: boolean; merchantName?: string; synced?: { items: number; categories: number } },
      { locationId: string; merchantId: string; accessToken: string }
    >({
      query: (body) => ({ url: '/clover/connect', method: 'POST', body }),
      // The connect call also pulls the menu, so those caches are stale too.
      invalidatesTags: ['Pos', 'Menu', 'Location']
    }),
    syncPosMenu: builder.mutation<
      { items: number; categories: number; modifiers: number },
      string
    >({
      query: (id) => ({ url: `/pos/${id}/sync`, method: 'POST' }),
      invalidatesTags: ['Pos', 'Menu']
    }),
    checkPosHealth: builder.query<{ ok: boolean; name?: string }, string>({
      query: (id) => `/pos/${id}/health`
    }),
    disconnectPos: builder.mutation<void, string>({
      query: (id) => ({ url: `/pos/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Pos']
    }),

    /* --------------------------------------------------- orders needing help */
    getOrdersNeedingAttention: builder.query<
      {
        data: {
          _id: string;
          orderNumber: string;
          status: string;
          customerName?: string;
          customerPhone?: string;
          totalCents: number;
          createdAt: string;
          callId?: string;
          externalReferenceId: string;
          externalPosOrderId?: string;
          reconciliationAttempts: number;
          lastError?: string;
          lastAttemptAt: string;
          items: string[];
        }[];
      },
      void
    >({
      query: () => '/orders/needs-attention',
      providesTags: ['Order']
    }),
    reconcileOrder: builder.mutation<
      { ok: boolean; adopted: boolean; status: string; externalPosOrderId?: string; error?: string },
      string
    >({
      query: (id) => ({ url: `/orders/${id}/reconcile`, method: 'POST' }),
      invalidatesTags: ['Order']
    }),

    /* ------------------------------------------------------------ pos status */
    getPosStatus: builder.query<
      {
        data: {
          locationId: string;
          locationName: string;
          provider: string;
          status:
            | 'CONNECTED'
            | 'DISCONNECTED'
            | 'ERROR'
            | 'SYNCING'
            | 'EXPIRED'
            | 'AWAITING_PARTNER_ACCESS';
          merchantId?: string;
          connectedAt?: string;
          lastSyncAt?: string;
          lastSyncItemCount?: number;
          lastSyncError?: string;
          lastOrderSentAt?: string;
          lastOrderNumber?: string;
          lastOrderExternalId?: string;
          ordersNeedingAttention: number;
          capabilities: Record<string, boolean>;
        }[];
      },
      void
    >({
      query: () => '/pos',
      providesTags: ['Pos']
    }),

    /* --------------------------------------------------------- order payment */
    getOrderPayment: builder.query<
      {
        orderStatus: string;
        totalCents: number;
        currency: string;
        posProvider?: string;
        externalPosOrderId?: string;
        attempts: {
          id: string;
          status: string;
          amountCents: number;
          currency: string;
          paymentProvider: string;
          paymentLink?: string;
          expiresAt?: string;
          paidAt?: string;
          smsSentAt?: string;
          smsDeliveryStatus?: string;
          posReconciliationStatus?: string;
          failureReason?: string;
        }[];
      },
      string
    >({
      query: (id) => `/orders/${id}/payment`,
      providesTags: ['Order']
    }),
    resendPaymentLink: builder.mutation<{ smsSent: boolean }, string>({
      query: (id) => ({ url: `/orders/${id}/payment-link/resend`, method: 'POST' }),
      invalidatesTags: ['Order']
    }),

    /* ---------------------------------------------------------------- team */
    getTeam: builder.query<{ data: MembershipRecord[] }, void>({
      query: () => '/users',
      providesTags: ['Team']
    }),
    inviteMember: builder.mutation<
      { message: string },
      { email: string; role: string; locationIds?: string[] }
    >({
      query: (body) => ({ url: '/auth/invite', method: 'POST', body }),
      invalidatesTags: ['Team']
    }),
    updateMember: builder.mutation<
      MembershipRecord,
      { id: string; role?: string; status?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/users/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Team']
    }),
    removeMember: builder.mutation<void, string>({
      query: (id) => ({ url: `/users/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Team']
    }),

    /* ------------------------------------------------------------- billing */
    getPlans: builder.query<{ data: PlanRecord[] }, void>({
      query: () => '/billing/plans',
      providesTags: ['Billing']
    }),
    getSubscription: builder.query<SubscriptionResponse, void>({
      query: () => '/subscriptions',
      providesTags: ['Billing']
    }),
    getInvoices: builder.query<{ data: InvoiceRecord[] }, void>({
      query: () => '/billing/invoices',
      providesTags: ['Billing']
    }),
    startCheckout: builder.mutation<
      { url: string },
      { planCode: string; interval: 'MONTH' | 'YEAR' }
    >({
      query: (body) => ({ url: '/billing/checkout', method: 'POST', body })
    }),
    openBillingPortal: builder.mutation<{ url: string }, void>({
      query: () => ({ url: '/billing/portal', method: 'POST' })
    }),

    /* --------------------------------------------------------------- usage */
    getDailyUsage: builder.query<
      { data: { _id: { date: string; type: string }; quantity: number }[] },
      void
    >({
      query: () => '/usage/daily',
      providesTags: ['Usage']
    }),
    getUsageByLocation: builder.query<
      { data: { _id: string; name?: string; minutes: number }[] },
      void
    >({
      query: () => '/usage/by-location',
      providesTags: ['Usage']
    })
  })
});

export const {
  useGetPosConnectionsQuery,
  useConnectCloverMutation,
  useSyncPosMenuMutation,
  useLazyCheckPosHealthQuery,
  useDisconnectPosMutation,
  useGetPosStatusQuery,
  useGetOrderPaymentQuery,
  useResendPaymentLinkMutation,
  useGetOrdersNeedingAttentionQuery,
  useReconcileOrderMutation,
  useGetTeamQuery,
  useInviteMemberMutation,
  useUpdateMemberMutation,
  useRemoveMemberMutation,
  useGetPlansQuery,
  useGetSubscriptionQuery,
  useGetInvoicesQuery,
  useStartCheckoutMutation,
  useOpenBillingPortalMutation,
  useGetDailyUsageQuery,
  useGetUsageByLocationQuery
} = accountApi;
